  import { useEffect, useState, useRef } from "react";
  import { useLocation, useNavigate } from "react-router-dom";
  import { Plus } from "lucide-react";
  import ClientList from "../features/clients/ClientList";
  import ClientDrawer from "../features/clients/ClientDrawer";
  import { clientService } from "../services/client.service";
  import UndoToast from "../ui/UndoToast";
  import DropdownMenu from "../ui/DropdownMenu";
  import ConfirmDeleteModal from "../ui/ConfirmDeleteModal";

  export default function ClientsPage({ onboardingContext }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    // 🔔 Undo toast state
    const [archivedClient, setArchivedClient] = useState(null);
    const [showToast, setShowToast] = useState(false);

    const [status, setStatus] = useState("all");
    const [statusAnchor, setStatusAnchor] = useState(null);

    const [city, setCity] = useState("all");
    const [cityAnchor, setCityAnchor] = useState(null);

    const [search, setSearch] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);

    const searchRef = useRef(null);

    useEffect(() => {
      loadClients();
    }, []);

    // Handle onboarding deep-link or command launcher
    useEffect(() => {
      if (onboardingContext?.action === 'create') {
        setSelectedClient(null);
        setOpen(true);
      }
    }, [onboardingContext]);

    // Handle URL parameters for direct navigation (e.g., from dashboard)
    useEffect(() => {
      const searchParams = new URLSearchParams(location.search);
      const action = searchParams.get('action');
      
      if (action === 'create') {
        setSelectedClient(null);
        setOpen(true);
        // Clean up URL parameters after opening drawer
        navigate('/clients', { replace: true });
      }
    }, [location.search, navigate]);

    async function loadClients() {
      const data = await clientService.list();
      setClients(data);
    }

    async function handleArchive(client) {
      // Optimistic update (KEEP client, mark archived)
      setClients((prev) =>
        prev.map((c) =>
          c.id === client.id ? { ...c, archived_at: new Date().toISOString() } : c
        )
      );

      setArchivedClient(client);
      setShowToast(true);

      try {
        await clientService.archive(client.id);
      } catch (err) {
        console.error("Archive failed", err);
        loadClients(); // fallback
      }
    }

    async function handleRestore(client) {
      // Optimistic update
      setClients((prev) =>
        prev.map((c) => (c.id === client.id ? { ...c, archived_at: null } : c))
      );

      try {
        await clientService.restore(client.id);
      } catch (err) {
        console.error("Restore failed", err);
        loadClients(); // fallback
      }
    }

    async function confirmDelete(client) {
      setDeleteTarget(client);
    }

    async function handleDeleteConfirmed() {
      if (!deleteTarget) return;

      const client = deleteTarget;

      // Optimistic remove
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      setDeleteTarget(null);

      try {
        await clientService.delete(client.id);
      } catch (err) {
        console.error("Delete failed", err);
        setClients((prev) => [client, ...prev]);
      }
    }

    function handleUndo() {
      if (!archivedClient) return;

      setClients((prev) =>
        prev.map((c) =>
          c.id === archivedClient.id ? { ...c, archived_at: null } : c
        )
      );

      setArchivedClient(null);
      setShowToast(false);
    }

    const visibleClients = clients
      // 🔍 SEARCH
      .filter((client) => {
        const q = search.toLowerCase().trim();
        if (!q) return true;

        const searchable = Object.values(client)
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchable.includes(q);
      })

      // 📌 STATUS
      .filter((client) => {
        if (status === "active") return !client.archived_at;
        if (status === "archived") return !!client.archived_at;
        return true;
      })

      // 🌍 CITY
      .filter((client) => {
        if (city === "all") return true;
        return client.city === city;
      })

      // ↕ SORT
      .sort((a, b) => {
        // Active first
        if (!!a.archived_at !== !!b.archived_at) {
          return a.archived_at ? 1 : -1;
        }
        // Newest first
        return new Date(b.created_at) - new Date(a.created_at);
      });

    useEffect(() => {
      function onKey(e) {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
          e.preventDefault();
          searchRef.current?.focus();
        }
      }
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
      <div className="space-y-4 relative max-w-full">
        {/* Onboarding Context Header */}
        {onboardingContext?.source === 'onboarding' && !clients.length && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-4">
            <p className="text-sm text-blue-900">
              <span className="font-medium">Getting started</span>
              <span className="text-blue-600 mx-2">→</span>
              <span>Add your first client</span>
            </p>
          </div>
        )}

        {/* Top controls */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Left: Filters */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <input
              ref={searchRef}
              placeholder="Search clients…  ⌘K"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="
      w-full max-w-[260px]
      px-3 py-2
      text-sm
      rounded-md
      border border-[var(--color-border)]
      focus:outline-none
      focus:ring-1 focus:ring-[var(--color-primary)]/40
    "
            />

            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setStatusAnchor(rect);
              }}
              className="
      px-3 py-2
      text-sm
      rounded-md
      border border-[var(--color-border)]
      bg-white
      flex items-center justify-between
      min-w-[140px]
      hover:bg-[var(--color-bg-muted)]
      flex-shrink-0
    "
            >
              {status === "all"
                ? "All"
                : status === "active"
                ? "Active"
                : "Archived"}
            </button>

            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setCityAnchor(rect);
              }}
              className="
      px-3 py-2
      text-sm
      rounded-md
      border border-[var(--color-border)]
      bg-white
      flex items-center justify-between
      min-w-[140px]
      hover:bg-[var(--color-bg-muted)]
      flex-shrink-0
    "
            >
              {city === "all" ? "All cities" : city}
            </button>
          </div>

          {/* Right: Primary action */}
          <button
            onClick={() => {
              setSelectedClient(null);
              setOpen(true);
            }}
            className="
        flex items-center gap-1.5
        px-3 py-2
        rounded-md
        bg-[var(--color-primary)]
        text-white
        text-sm
        font-medium
        shadow-sm
        hover:opacity-95
        flex-shrink-0
      "
          >
            <Plus size={16} />
            Add Client
          </button>
        </div>

        <ClientList
          clients={visibleClients}
          onSelect={(client) => {
            setSelectedClient(client);
            setOpen(true);
          }}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onDelete={confirmDelete}
        />

        {/* Drawer */}
        {open && (
          <ClientDrawer
            client={selectedClient}
            onClose={() => setOpen(false)}
            onSaved={loadClients}
          />
        )}

        {/* Undo Toast */}
        {showToast && archivedClient && (
          <UndoToast
            message={`Client "${archivedClient.name}" archived`}
            onUndo={handleUndo}
            onClose={() => {
              setShowToast(false);
              setArchivedClient(null);
            }}
          />
        )}

        {statusAnchor && (
          <DropdownMenu
            anchorRect={statusAnchor}
            onClose={() => setStatusAnchor(null)}
          >
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "archived", label: "Archived" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  setStatus(item.key);
                  setStatusAnchor(null);
                }}
                className={`
            w-full
            px-3 py-2
            text-left
            hover:bg-[var(--color-bg-muted)]
            ${
              status === item.key ? "text-[var(--color-primary)] font-medium" : ""
            }
          `}
              >
                {item.label}
              </button>
            ))}
          </DropdownMenu>
        )}

        {cityAnchor && (
          <DropdownMenu
            anchorRect={cityAnchor}
            onClose={() => setCityAnchor(null)}
          >
            {["all", "Pune", "Mumbai"].map((item) => (
              <button
                key={item}
                onClick={() => {
                  setCity(item);
                  setCityAnchor(null);
                }}
                className={`
            w-full
            px-3 py-2
            text-left
            hover:bg-[var(--color-bg-muted)]
            ${city === item ? "text-[var(--color-primary)] font-medium" : ""}
          `}
              >
                {item === "all" ? "All cities" : item}
              </button>
            ))}
          </DropdownMenu>
        )}

        <ConfirmDeleteModal
          open={!!deleteTarget}
          title={`Delete "${deleteTarget?.name}" permanently?`}
          description="This action cannot be undone. All client data will be permanently removed."
          confirmLabel="Delete permanently"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirmed}
        />
      </div>
    );
  }
